import React, { type PropsWithChildren, useMemo } from 'react'

import { useIdentity } from '@vp/ubik-context'

export type AuthorizationContext = {
  authorizationHeader?: string
  shopperId?: string
}

export const AuthorizationProviderContext = React.createContext<AuthorizationContext | undefined>(undefined)

export function useAuthorization() {
  const ctx = React.useContext(AuthorizationProviderContext)
  if (!ctx) {
    throw new Error('useAuthorization must be used within an AuthorizationProvider')
  }
  return ctx
}

export function AuthorizationProvider(props: PropsWithChildren<{ authorizationHeader?: string }>) {
  const identityCtx = useIdentity()

  // Usually we'll get the auth token from the identity context, but for local development, the auth token can be passed
  // in the server-side data.
  const authorizationHeader = props.authorizationHeader ?? identityCtx?.identity?.authorizationHeader
  const shopperId = identityCtx?.auth?.getCanonicalId() ?? undefined

  const context = useMemo(
    () => ({
      authorizationHeader,
      shopperId,
    }),
    [authorizationHeader, shopperId],
  )

  return <AuthorizationProviderContext.Provider value={context}>{props.children}</AuthorizationProviderContext.Provider>
}
